import React from 'react'

import {GetServerSideProps} from 'next'
import {QueryListenerOptions} from 'react-datocms'
import {DatocmsContent} from 'src/components/content/DatocmsContent'
import PageBreadCrumb from 'src/components/content/PageBreadCrumb'
import {WebPage, NotFound} from 'src/components/layout'
import {WebPageCommonProps} from 'src/components/layout/WebPage'
import {makePageCommonProps} from 'src/utils'
import urljoin from 'url-join'
import settings from '@festi/common/constants/settings'
import {pageQuery} from '@festi/common/api/datocms/queries'
import {Page} from '@festi/common/api/datocms/types/Page'
import {
  makeSubscriptionProps,
  usePageSubscription,
} from '@festi/common/api/datocms/utils'
import {extractSeoFromDatoPage} from '@festi/common/utils/seo'

interface PageParams {
  path: string[]
}

interface Props {
  href: string
  common: WebPageCommonProps
  subscription: QueryListenerOptions<Page, {path: string}>
}

export const getServerSideProps: GetServerSideProps = async ({
  res,
  params,
  preview = false,
}) => {
  const {path} = params as unknown as PageParams
  const pathString = path.join('/')

  const [common, subscription] = await Promise.all([
    makePageCommonProps(preview),
    makeSubscriptionProps<Page>(preview, pageQuery, {
      path: `/${pathString}`,
    }),
  ])

  if (!subscription?.initialData?.page) {
    return {
      notFound: true,
    }
  } else if (common.categories.length === 0) {
    res.setHeader('Cache-Control', 's-maxage=1, stale-while-revalidate')
  } else {
    res.setHeader('Cache-Control', 's-maxage=60, stale-while-revalidate=3600')
  }

  // DutyFree dato pages should have canonical to elko.is as the content is the same
  const baseUrl = settings.baseUrl

  const href = urljoin(baseUrl, ...path)

  return {
    props: {
      href,
      subscription,
      common,
    },
  }
}

export default function DynamicPage({
  href,
  subscription,
  common,
}: Props): JSX.Element {
  const {data} = usePageSubscription(subscription)
  const {page} = data

  if (!page) {
    return (
      <WebPage
        common={common}
        nextSeo={{
          seoNoIndex: true,
          seoTitle: '404 - Síða fannst ekki',
        }}
        backgroundColor="white"
      >
        <NotFound />
      </WebPage>
    )
  }

  const {seoTitle, seoDescription, seoImages} = extractSeoFromDatoPage(page)

  return (
    <WebPage
      common={common}
      nextSeo={{
        seoNoIndex: page?.seoIndexing === false ? true : undefined,
        seoTitle,
        seoDescription,
        seoImages,
        seoCanonical: href,
      }}
    >
      <PageBreadCrumb page={page} />
      <DatocmsContent content={page.content} />
    </WebPage>
  )
}
